import { type ApiSchemas, call } from '@rental/contracts/client'
import { isKnownDisputeStatus } from '@rental/domain'
import { format, formatDate, formatDateTime, formatEuro, formatMonth } from '@rental/i18n'
import { Field } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import type { ReactNode } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { AUTH_MODE, api, load } from '../api'
import { StatusBadge } from '../components/BookingItem'
import { type Column, DataTable } from '../components/DataTable'
import { PageHeader } from '../components/PageHeader'
import { PendingLine } from '../components/PendingBadge'
import { Fetching, useAnswered } from '../components/Results'
import { SearchForm, useSearchFields } from '../components/SearchForm'
import { Section } from '../components/Section'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { SubPanel } from '../components/SubPanel'
import { Guard, LoadState } from '../components/states'
import { TabLinks, tabClass } from '../components/TabLinks'
import { TechnicalLines } from '../components/TechnicalDetails'
import { FieldPair, PAIRED_FIELD, SHORT_FIELD, StaffInput, StaffSelect } from '../components/ui'
import { filePath, type StaffFile } from '../files'
import { deadlineTone, deadlineWords, statusTone } from '../fines/fines'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { BookingCode } from '../reports/BookingCode'
import { defaultRange } from '../reports/range'
import { useRomeToday } from '../rome-today'

const route = getRouteApi('/reports')

type Revenue = ApiSchemas['StaffRevenue']
type Figures = Revenue['totals']
type RevenueRow = Revenue['rows'][number]
type Problem = NonNullable<ApiSchemas['StaffOpenItems']['payable']['rows'][number]['problem']>

/** A refund that failed wants a hand now; one waiting for an action or held by a dispute, soon. */
const PROBLEM_TONE: Readonly<Record<Problem, ChipTone>> = {
  failed: 'danger',
  requires_action: 'warning',
  disputed: 'warning',
}

/**
 * S5c (RPT-1, RPT-3, RPT-4): the money reports, for the holders of
 * reports.view. Ricavi: money received and rentals' value side by side, by
 * Rome day or month and category (Fady, 15 Oct). Aperti: what is still open
 * today. Each downloads as CSV for Italian Excel from the web app. Booking
 * codes and plates, never a customer's name. UI-2e: the views are links, the
 * period follows the address, and every table is a region the keyboard scrolls.
 */
export function ReportsScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const search = route.useSearch()
  const tab = search.tab ?? 'revenue'
  return (
    <Guard allowed={can(access, 'reports.view')} title={m.reports.title}>
      <PageHeader title={m.reports.title} intro={m.reports.intro}>
        <div className="pt-2">
          <TabLinks label={m.reports.title}>
            {(['revenue', 'open'] as const).map((key) => (
              <Link
                key={key}
                to="/reports"
                search={{ ...search, tab: key === 'open' ? 'open' : undefined }}
                // As Multe's: the router's own mark, exactly, or Ricavi stays current on Aperti.
                activeOptions={{ exact: true }}
                aria-current={tab === key ? 'page' : undefined}
                className={tabClass(tab === key)}
              >
                {m.reports.tabs[key]}
              </Link>
            ))}
          </TabLinks>
        </div>
      </PageHeader>
      {tab === 'open' ? <OpenReport /> : <RevenueReport />}
    </Guard>
  )
}

/**
 * The CSV: a link in the web app, which sends its cookies; the iPad app has
 * nowhere to save it yet, and says so instead of offering a dead button.
 */
function CsvLink({ file }: { file: StaffFile }) {
  const { messages: m } = useLocale()
  if (AUTH_MODE === 'token') return <PendingLine>{m.reports.csvPending}</PendingLine>
  return (
    <a href={filePath(file)} download className="flex min-h-touch items-center gap-2 self-start">
      <Download aria-hidden="true" className="size-6" />
      {m.reports.csv}
    </a>
  )
}

/** When it was made, and that the next is on its way while the last stays in sight. */
function Generated({ at, fetching }: { at: string; fetching: boolean }) {
  const { locale, messages: m } = useLocale()
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <p className="text-sm text-text-muted">
        {format(m.reports.generatedAt, { when: formatDateTime(locale, at) })}
      </p>
      {fetching ? <Fetching /> : null}
    </div>
  )
}

function RevenueReport() {
  const { locale, messages: m } = useLocale()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  // Today follows the clock (UI-2g). An end asked for alone takes its own month up to it:
  // from this month's first day, a past end made a range that ended before it began.
  const today = useRomeToday()
  const to = search.to ?? today
  const from = search.from ?? defaultRange(to).from
  const by = search.by ?? 'day'
  // Back, or a link to another period: the fields follow the address.
  const [form, setForm] = useSearchFields({ from, to, by })
  const report = useQuery({
    queryKey: ['reports', 'revenue', from, to, by],
    queryFn: () =>
      load(call(api.GET('/v1/staff/reports/revenue', { params: { query: { from, to, by } } }))),
    placeholderData: keepPreviousData,
  })
  const fetching = report.isPlaceholderData
  // The period on screen, said once its answer is in, as a list's count is (UI-2g): Mostra
  // keeps the focus, and the tables change under it unsaid. Never sooner, never for a refusal.
  const answered = useAnswered(`${from} ${to} ${by}`, fetching)
  const shownPeriod = (asked: string) => {
    const [shownFrom = '', shownTo = '', shownBy] = asked.split(' ')
    return format(m.reports.shown, {
      from: formatDate(locale, shownFrom),
      to: formatDate(locale, shownTo),
      by: shownBy === 'month' ? m.reports.byMonth : m.reports.byDay,
    })
  }
  const euro = (cents: number) => formatEuro(cents, locale)
  const strong = (cents: number) => <strong>{euro(cents)}</strong>
  /** The figures, the same for a row and for the totals. */
  const figures: readonly Column<Figures>[] = [
    { header: m.reports.channels.cash, cell: (line) => euro(line.received.cash), numeric: true },
    { header: m.reports.channels.pos, cell: (line) => euro(line.received.pos), numeric: true },
    {
      header: m.reports.channels.transfer,
      cell: (line) => euro(line.received.transfer),
      numeric: true,
    },
    {
      header: m.reports.channels.stripe,
      cell: (line) => euro(line.received.stripe),
      numeric: true,
    },
    { header: m.reports.received, cell: (line) => strong(line.receivedCents), numeric: true },
    { header: m.reports.handovers, cell: (line) => line.handovers, numeric: true },
    { header: m.reports.handedOver, cell: (line) => euro(line.handedOverCents), numeric: true },
    { header: m.reports.fees, cell: (line) => euro(line.feesCents), numeric: true },
    { header: m.reports.waived, cell: (line) => euro(line.waivedCents), numeric: true },
    { header: m.reports.fineFees, cell: (line) => euro(line.fineFeesCents), numeric: true },
    { header: m.reports.rented, cell: (line) => strong(line.rentedCents), numeric: true },
  ]

  return (
    <Section title={m.reports.tabs.revenue}>
      <SearchForm
        label={m.reports.tabs.revenue}
        submitLabel={m.reports.show}
        filtered={search.from !== undefined || search.to !== undefined || search.by !== undefined}
        onSubmit={() =>
          void navigate({
            to: '/reports',
            search: {
              ...search,
              from: form.from || undefined,
              to: form.to || undefined,
              by: form.by === 'month' ? 'month' : undefined,
            },
          })
        }
        onClear={() => void navigate({ to: '/reports', search: { tab: search.tab } })}
      >
        <FieldPair>
          <Field className={PAIRED_FIELD} label={m.reports.from} required>
            {(control) => (
              <StaffInput
                {...control}
                type="date"
                value={form.from}
                onChange={(event) => setForm({ ...form, from: event.target.value })}
              />
            )}
          </Field>
          <Field className={PAIRED_FIELD} label={m.reports.to} required>
            {(control) => (
              <StaffInput
                {...control}
                type="date"
                value={form.to}
                onChange={(event) => setForm({ ...form, to: event.target.value })}
              />
            )}
          </Field>
        </FieldPair>
        <Field className={SHORT_FIELD} label={m.reports.by}>
          {(control) => (
            <StaffSelect
              {...control}
              value={form.by}
              onChange={(event) =>
                setForm({ ...form, by: event.target.value === 'month' ? 'month' : 'day' })
              }
            >
              <option value="day">{m.reports.byDay}</option>
              <option value="month">{m.reports.byMonth}</option>
            </StaffSelect>
          )}
        </Field>
      </SearchForm>
      <p role="status" className="sr-only">
        {report.data && !report.isError ? (
          <span key={answered}>{shownPeriod(answered)}</span>
        ) : null}
      </p>
      <LoadState data={report.data} problem={problemOf(report.error)} bare>
        {(data) => {
          const names = new Map(data.categories.map((category) => [category.id, category.name]))
          const caption = `${m.reports.tabs.revenue}: ${formatDate(locale, data.from, 'short')} – ${formatDate(locale, data.to, 'short')}`
          const period = (value: string) =>
            data.by === 'month' ? formatMonth(locale, value) : formatDate(locale, value, 'short')
          const totals = data.totals
          return (
            <>
              <Generated at={data.generatedAt} fetching={fetching} />
              {data.rows.length === 0 ? (
                <p className="text-text-muted">{m.reports.empty}</p>
              ) : (
                <div aria-busy={fetching || undefined}>
                  <DataTable
                    label={caption}
                    caption={caption}
                    testId="revenue-table"
                    rows={data.rows}
                    rowKey={(row: RevenueRow) => `${row.period}:${row.categoryId}`}
                    columns={[
                      {
                        header: m.reports.period,
                        cell: (row: RevenueRow) => period(row.period),
                        rowHeader: true,
                        className: 'whitespace-nowrap',
                      },
                      {
                        header: m.reports.category,
                        // A row is a period and a category: both name it (UI-2g).
                        rowHeader: true,
                        // A category gone from the list since is said as such, never as its id.
                        cell: (row: RevenueRow) =>
                          names.get(row.categoryId) ?? (
                            <span className="text-text-muted">{m.reports.categoryGone}</span>
                          ),
                      },
                      ...figures,
                    ]}
                    foot={{
                      label: m.reports.total,
                      span: 2,
                      cells: figures.map((column) =>
                        column.header === m.reports.received ? (
                          <span key={column.header} data-testid="revenue-received">
                            {column.cell(totals)}
                          </span>
                        ) : (
                          column.cell(totals)
                        ),
                      ),
                    }}
                  />
                </div>
              )}
              <p className="text-sm text-text-muted">{m.reports.note}</p>
              <CsvLink file={{ kind: 'report', report: 'revenue', from, to, by }} />
            </>
          )
        }}
      </LoadState>
    </Section>
  )
}

function OpenReport() {
  const { locale, messages: m } = useLocale()
  const report = useQuery({
    queryKey: ['reports', 'open'],
    queryFn: () => load(call(api.GET('/v1/staff/reports/open'))),
  })
  const o = m.reports.open
  // The fiscal list's columns are Fiscale's, by Fiscale's names (UI-2g).
  const missing = m.fiscal.missing
  const booking = (code: string | null) => <BookingCode code={code} />
  const when = (instant: string | null) => (instant ? formatDateTime(locale, instant) : '–')
  const plate = (value: string | null) => <span className="figures">{value ?? '–'}</span>

  return (
    <Section title={m.reports.tabs.open}>
      <LoadState data={report.data} problem={problemOf(report.error)} bare>
        {(data) => (
          <>
            <Generated at={data.generatedAt} fetching={false} />
            <OpenList
              title={o.deposits}
              testId="open-deposits"
              rows={data.deposits}
              rowKey={(deposit) => `${deposit.code}:${deposit.method}`}
              columns={[
                { header: o.booking, cell: (deposit) => booking(deposit.code), rowHeader: true },
                { header: o.plate, cell: (deposit) => plate(deposit.plate) },
                {
                  header: o.state,
                  // A state as a chip, as everywhere (UI-2g): held or waiting, it is information.
                  cell: (deposit) => (
                    <StatusChip tone="info">{o.depositStates[deposit.state]}</StatusChip>
                  ),
                },
                { header: o.method, cell: (deposit) => m.deposit.methods[deposit.method] },
                {
                  header: o.amount,
                  cell: (deposit) => formatEuro(deposit.amountCents, locale),
                  numeric: true,
                },
                { header: o.due, cell: (deposit) => when(deposit.captureBefore) },
              ]}
            />
            <OpenList
              title={o.receivable}
              testId="open-receivable"
              listed={data.receivable}
              rows={data.receivable.rows}
              rowKey={(row) => row.code}
              columns={[
                { header: o.booking, cell: (row) => booking(row.code), rowHeader: true },
                { header: o.state, cell: (row) => <StatusBadge status={row.status} /> },
                {
                  header: o.amount,
                  cell: (row) => formatEuro(row.balanceCents, locale),
                  numeric: true,
                },
              ]}
            />
            <OpenList
              title={o.payable}
              testId="open-payable"
              listed={data.payable}
              rows={data.payable.rows}
              rowKey={(row) => row.code}
              columns={[
                { header: o.booking, cell: (row) => booking(row.code), rowHeader: true },
                {
                  header: o.state,
                  cell: (row) => (
                    <span className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={row.status} />
                      {row.problem ? (
                        <StatusChip tone={PROBLEM_TONE[row.problem]}>
                          {o.problems[row.problem]}
                        </StatusChip>
                      ) : null}
                    </span>
                  ),
                },
                {
                  header: o.amount,
                  cell: (row) => formatEuro(row.creditCents, locale),
                  numeric: true,
                },
              ]}
            />
            <OpenList
              title={o.disputes}
              testId="open-disputes"
              rows={data.disputes}
              rowKey={(dispute) => dispute.stripeDisputeId}
              columns={[
                { header: o.booking, cell: (dispute) => booking(dispute.code), rowHeader: true },
                {
                  header: o.reference,
                  cell: (dispute) => <span className="figures">{dispute.stripeDisputeId}</span>,
                },
                {
                  header: o.state,
                  // A status Stripe adds after this app is said in words; its code is the Owner's (C5).
                  // Every one listed here is still open: the booking page's tone (UI-2g).
                  cell: (dispute) => (
                    <StatusChip tone="warning">
                      {isKnownDisputeStatus(dispute.status)
                        ? m.disputes.status[dispute.status]
                        : m.disputes.statusUnknown}
                    </StatusChip>
                  ),
                },
                {
                  header: o.amount,
                  cell: (dispute) => formatEuro(dispute.amountCents, locale),
                  numeric: true,
                },
                { header: o.due, cell: (dispute) => when(dispute.evidenceDueBy) },
              ]}
              more={
                <TechnicalLines
                  surface="plain"
                  lines={data.disputes
                    .filter((dispute) => !isKnownDisputeStatus(dispute.status))
                    .map(
                      (dispute) =>
                        `${dispute.stripeDisputeId}: ${format(m.app.stripeCode, { code: dispute.status })}`,
                    )}
                />
              }
            />
            <OpenList
              title={o.fiscal}
              testId="open-fiscal"
              listed={data.fiscal}
              rows={data.fiscal.rows}
              rowKey={(row) => row.id}
              columns={[
                { header: missing.booking, cell: (row) => booking(row.code), rowHeader: true },
                { header: missing.money, cell: (row) => m.fiscal.money[row.money] },
                { header: missing.method, cell: (row) => m.fiscal.methods[row.method] },
                {
                  header: missing.amount,
                  cell: (row) => formatEuro(row.amountCents, locale),
                  numeric: true,
                },
                { header: missing.when, cell: (row) => when(row.at) },
              ]}
            />
            <OpenList
              title={o.fines}
              testId="open-fines"
              listed={data.fines}
              summary={format(o.finesCount, {
                overdue: data.fines.overdue,
                dueSoon: data.fines.dueSoon,
              })}
              rows={data.fines.rows}
              rowKey={(row) => `${row.authority}:${row.noticeNumber}`}
              columns={[
                {
                  header: o.reference,
                  cell: (row) => `${row.authority} · ${row.noticeNumber}`,
                  rowHeader: true,
                },
                {
                  header: o.booking,
                  cell: (row) =>
                    row.code ? (
                      booking(row.code)
                    ) : (
                      <span className="text-text-muted">{m.morningEmail.fineNoBooking}</span>
                    ),
                },
                { header: o.plate, cell: (row) => plate(row.plate) },
                {
                  header: o.state,
                  cell: (row) => (
                    <span className="flex flex-wrap items-center gap-2">
                      <StatusChip tone={statusTone(row.status)}>
                        {m.fines.status[row.status]}
                      </StatusChip>
                      <StatusChip tone={deadlineTone(row.deadline.state)}>
                        {deadlineWords(row.deadline, locale, m)}
                      </StatusChip>
                    </span>
                  ),
                },
                { header: o.due, cell: (row) => formatDate(locale, row.deadline.on, 'short') },
              ]}
            />
            <CsvLink file={{ kind: 'report', report: 'open' }} />
          </>
        )}
      </LoadState>
    </Section>
  )
}

/**
 * One of Aperti's lists, a part of its pane with its own heading: a table in
 * a region the keyboard can scroll, its total when it has one, a line of
 * counts when it has those, and how many the CSV adds.
 */
function OpenList<Row>({
  title,
  testId,
  columns,
  rows,
  rowKey,
  listed,
  summary,
  more,
}: {
  title: string
  testId: string
  columns: readonly Column<Row>[]
  rows: readonly Row[]
  rowKey: (row: Row) => string
  listed?: { count: number; totalCents?: number; rows: readonly unknown[] }
  summary?: string
  /** Under the table: what only whoever looks after the system reads. */
  more?: ReactNode
}) {
  const { locale, messages: m } = useLocale()
  const o = m.reports.open
  return (
    <div data-testid={testId}>
      <SubPanel
        title={
          <>
            {title}
            {listed?.totalCents !== undefined ? (
              <span className="figures"> · {formatEuro(listed.totalCents, locale)}</span>
            ) : null}
          </>
        }
      >
        {summary && rows.length > 0 ? <p className="text-sm">{summary}</p> : null}
        {rows.length === 0 ? (
          <p className="text-text-muted">{o.none}</p>
        ) : (
          <DataTable label={title} columns={columns} rows={rows} rowKey={rowKey} />
        )}
        {listed && listed.count > listed.rows.length ? (
          <p className="text-sm text-text-muted">
            {format(o.shown, { shown: listed.rows.length, count: listed.count })}
          </p>
        ) : null}
        {more}
      </SubPanel>
    </div>
  )
}
